import { useMemo } from "react";
import { useStore } from "@/app/store";
import { monthLabels } from "@/constants/months";
import type {
  CellStatus,
  ResultCell,
  ResultRow,
} from "../components/CalculationResultsTable/CalculationResultsTable.types";

interface UseResultRowsResult {
  monthColumns: string[];
  resultRows: ResultRow[];
}

const missingCell = (): ResultCell => ({
  value: null,
  status: "MISSING_INPUT",
});

const useResultRows = (
  calculationId: string,
  activeSubcategoryId: string,
): UseResultRowsResult => {
  const calculation = useStore((s) =>
    s.calculation?.id === calculationId ? s.calculation : null,
  );
  const monthColumns = useMemo<string[]>(() => {
    if (!calculation) return [];
    const start = parseInt(calculation.startDate.split("-")[1], 10);
    const end = parseInt(calculation.endDate.split("-")[1], 10);
    return monthLabels.slice(start - 1, end) as string[];
  }, [calculation]);

  const resultRows = useMemo<ResultRow[]>(() => {
    if (!calculation) return [];
    const category = calculation.categories.find(
      (c) => c.subCategoryDefinitionId === activeSubcategoryId,
    );
    if (!category) return [];

    const hasDimension = category.dimensionTypeId !== null;
    const start = parseInt(calculation.startDate.split("-")[1], 10);
    const end = parseInt(calculation.endDate.split("-")[1], 10);
    const monthSpan = end - start + 1;
    const emptyMonthlyCells = (): ResultCell[] =>
      Array.from({ length: monthSpan }, () => missingCell());

    if (!category.result) {
      if (!hasDimension) return [];
      if (category.dimensionValues.length === 0) return [];
      return category.dimensionValues.map((value) => ({
        label: value,
        monthlyValues: emptyMonthlyCells(),
        total: missingCell(),
      }));
    }

    const bucketResults = category.result.bucketResults;
    if (hasDimension && bucketResults.length === 0) return [];

    const bucketRows: ResultRow[] = bucketResults.map((bucket) => {
      const cellsByMonth = new Map<number, ResultCell>(
        bucket.monthlyAggregates.map((a) => [
          a.monthNumber,
          { value: a.cell.value, status: a.cell.status as CellStatus },
        ]),
      );
      return {
        label: bucket.dimensionValue ?? category.subCategoryName,
        monthlyValues: Array.from(
          { length: monthSpan },
          (_, i) => cellsByMonth.get(start + i) ?? missingCell(),
        ),
        total: {
          value: bucket.total.value,
          status: bucket.total.status as CellStatus,
        },
      };
    });

    if (!hasDimension) {
      if (bucketRows.length === 0) return [];
      return [{ ...bucketRows[0], label: "Total" }];
    }

    const totalCellsByMonth = new Map<number, ResultCell>(
      category.result.monthlyAggregates.map((a) => [
        a.monthNumber,
        { value: a.cell.value, status: a.cell.status as CellStatus },
      ]),
    );
    const totalRow: ResultRow = {
      label: "Total",
      monthlyValues: Array.from(
        { length: monthSpan },
        (_, i) => totalCellsByMonth.get(start + i) ?? missingCell(),
      ),
      total: {
        value: category.result.total.value,
        status: category.result.total.status as CellStatus,
      },
    };
    return [...bucketRows, totalRow];
  }, [calculation, activeSubcategoryId]);

  return { monthColumns, resultRows };
};

export default useResultRows;
